import React, { useState } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { Download, PlayCircle, Lock } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";
import { runPayrollAsync } from "../../store/slices/dataSlice";
import { ROLES_CONFIG, PayrollRecord } from "../../types";
import StatCard from "../../components/shared/StatCard";
import StatusBadge from "../../components/shared/StatusBadge";

const payStats = [
  {
    label: "Total Payroll",
    value: "₹18.4L",
    icon: "Payments",
    delta: "March 2026",
    deltaType: "neutral" as const,
    color: "#85D1DB",
    iconBg: "rgba(133,209,219,0.1)",
  },
  {
    label: "Processed",
    value: "5",
    icon: "CheckCircle",
    delta: "Of 6",
    deltaType: "up" as const,
    color: "#16a34a",
    iconBg: "#dcfce7",
  },
  {
    label: "Pending",
    value: "1",
    icon: "PendingActions",
    delta: "Processing",
    deltaType: "neutral" as const,
    color: "#d97706",
    iconBg: "#fef3c7",
  },
  {
    label: "Avg Salary",
    value: "₹88K",
    icon: "TrendingUp",
    delta: "+6% YoY",
    deltaType: "up" as const,
    color: "#6BB8C3",
    iconBg: "#C9FDF2",
  },
];

const DEPT_COLORS: Record<string, string> = {
  Engineering: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
  Finance: "linear-gradient(135deg,#22c55e,#16a34a)",
  Sales: "linear-gradient(135deg,#85D1DB,#ec4899)",
  Marketing: "linear-gradient(135deg,#6BB8C3,#85D1DB)",
  HR: "linear-gradient(135deg,#f59e0b,#ef4444)",
  Operations: "linear-gradient(135deg,#f43f5e,#f59e0b)",
};

const fmtINR = (n: number) => `₹${n.toLocaleString("en-IN")}`;

// Using global theme for table headers

const Payroll: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const payroll = useAppSelector((s) => s.data.payroll);
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG]
    : null;
  const canViewSalary = roleConfig?.canViewSalary ?? false;
  const canProcess = roleConfig?.canProcessPayroll ?? false;
  const [selected, setSelected] = useState<PayrollRecord | null>(
    payroll[0] || null,
  );

  return (
    <Box>
      <SEO title="Payroll" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {payStats.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        {/* List */}
        <Grid item xs={12} md={5}>
          <Card sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "14px 18px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                March 2026 Payroll
              </Typography>
              {canProcess && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayCircle sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    dispatch(runPayrollAsync())
                      .unwrap()
                      .then(() => {
                        dispatch(showSnackbar({ message: "Payroll processed successfully for all employees" }));
                      })
                      .catch((err) => {
                        dispatch(showSnackbar({ message: err || "Failed to process payroll", severity: "error" }));
                      });
                  }}
                  sx={{ fontSize: 12, borderRadius: 1.5 }}
                >
                  Run Payroll
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Employee", "Dept", "Net Pay", "Status"].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payroll.map((r) => (
                    <TableRow
                      key={r.id}
                      onClick={() => setSelected(r)}
                      sx={{
                        cursor: "pointer",
                        background:
                          selected?.id === r.id
                            ? "rgba(133,209,219,0.06)"
                            : "transparent",
                        transition: "background 0.15s",
                        "&:hover": { background: "rgba(133,209,219,0.04)" },
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background:
                                DEPT_COLORS[r.dept] || DEPT_COLORS.Engineering,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#fff",
                              flexShrink: 0,
                            }}
                          >
                            {r.name[0]}
                            {r.name.split(" ")[1]?.[0] || ""}
                          </Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                            {r.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 12,
                          color: "#64748b",
                        }}
                      >
                        {r.dept}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 13,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontWeight: 700,
                        }}
                      >
                        {canViewSalary ? fmtINR(r.net) : "••••••"}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <StatusBadge status={r.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Payslip */}
        <Grid item xs={12} md={7}>
          {selected && (
            <Card>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "14px 18px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  Payslip — {selected.name}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download sx={{ fontSize: 16 }} />}
                  onClick={() =>
                    dispatch(showSnackbar({ message: "Payslip downloaded" }))
                  }
                  sx={{ fontSize: 12, borderRadius: 1.5 }}
                >
                  Download
                </Button>
              </Box>
              {canViewSalary ? (
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2.5,
                      pb: 2,
                      borderBottom: "2px dashed #e5e7eb",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        HRCore Technologies Pvt. Ltd.
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}
                      >
                        Payslip for {selected.month}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {selected.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {selected.dept}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          background: "#f8fafc",
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid #e5e7eb",
                          height: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color: "#16a34a",
                            mb: 1.5,
                          }}
                        >
                          Earnings
                        </Typography>
                        {[
                          ["Basic Salary", selected.basic],
                          ["HRA", selected.hra],
                          ["Allowances", selected.allowances],
                        ].map(([k, v]) => (
                          <Box
                            key={k as string}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 12.5, color: "#475569" }}
                            >
                              {k}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5 }}>
                              {fmtINR(v as number)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            borderTop: "1px solid #e5e7eb",
                            pt: 1,
                            mt: 0.5,
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            Total
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: "'JetBrains Mono',monospace",
                            }}
                          >
                            {fmtINR(
                              selected.basic +
                                selected.hra +
                                selected.allowances,
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          background: "#f8fafc",
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid #e5e7eb",
                          height: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color: "#dc2626",
                            mb: 1.5,
                          }}
                        >
                          Deductions
                        </Typography>
                        {[
                          ["PF", selected.pf],
                          ["Income Tax", selected.tax],
                        ].map(([k, v]) => (
                          <Box
                            key={k as string}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 12.5, color: "#475569" }}
                            >
                              {k}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12.5, color: "#dc2626" }}
                            >
                              −{fmtINR(v as number)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            borderTop: "1px solid #e5e7eb",
                            pt: 1,
                            mt: 0.5,
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            Total
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#dc2626",
                              fontFamily: "'JetBrains Mono',monospace",
                            }}
                          >
                            {fmtINR(selected.pf + selected.tax)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
                      borderRadius: 2,
                      p: "16px 20px",
                      color: "#fff",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      Net Take-Home
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {fmtINR(selected.net)}
                    </Typography>
                  </Box>
                </CardContent>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    color: "#94a3b8",
                  }}
                >
                  <Lock sx={{ fontSize: 48, mb: 1.5 }} />
                  <Typography sx={{ fontSize: 14 }}>
                    You don't have permission to view salary details
                  </Typography>
                </Box>
              )}
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Payroll;
